/**
 * Temporal Parking Types - Step 0+1 Implementation
 * Based on plans/ramp-temporal-parking.md
 */

import { Aircraft } from "../../../types";
import { Shape } from "../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";

// Time bucket index (0 to TOTAL_SLOTS-1)
export type Bucket = number;

// Time conversion constants
export const MINUTES_PER_BUCKET = 15;
export const BUCKETS_PER_HOUR = 4;

// Staging area configuration defaults (Phase 3)
/**
 * Default minimum dwell time threshold for using staging workflow
 * Aircraft with dwell time below this threshold bypass staging (quick turns)
 */
export const DEFAULT_QUICK_TURN_THRESHOLD_MINUTES = 120;

/**
 * Default duration for arrival staging operations
 * Time allocated for unloading, customs, refueling, and post-flight servicing
 */
export const DEFAULT_ARRIVAL_STAGING_DWELL_MINUTES = 60;

/**
 * Default duration for departure staging operations
 * Time allocated for boarding, final checks, and pre-flight preparation
 */
export const DEFAULT_DEPARTURE_STAGING_DWELL_MINUTES = 30;

// Staging area configuration validation bounds (Phase 3)
/**
 * Minimum and maximum allowed values for arrival staging dwell time
 * Range: 15-180 minutes (0.25-3 hours)
 */
export const MIN_ARRIVAL_STAGING_MINUTES = 15;
export const MAX_ARRIVAL_STAGING_MINUTES = 180;

/**
 * Minimum and maximum allowed values for departure staging dwell time
 * Range: 15-120 minutes (0.25-2 hours)
 */
export const MIN_DEPARTURE_STAGING_MINUTES = 15;
export const MAX_DEPARTURE_STAGING_MINUTES = 120;

/**
 * Minimum and maximum allowed values for quick turn threshold
 * Range: 30-360 minutes (0.5-6 hours)
 */
export const MIN_QUICK_TURN_THRESHOLD_MINUTES = 30;
export const MAX_QUICK_TURN_THRESHOLD_MINUTES = 360;

// Aircraft size category based on maximum dimension (wingspan or length)
// Used for spot compatibility and parking policy enforcement
export type SizeCategory =
  | "very-small" // max dimension ≤40ft (Cessna 172, Piper PA-28, Cirrus SR22)
  | "small" // max dimension ≤60ft (Citations, Phenom 300, King Air 350, Learjet 45)
  | "midsize" // max dimension ≤70ft (Challenger 604/605, Falcon 900, Gulfstream G280)
  | "super-midsize" // max dimension ≤100ft (Falcon 7X, Gulfstream G500/G650)
  | "large" // max dimension ≤120ft (Global 6000/7500, Gulfstream G700, BBJ)
  | "enormous"; // max dimension >120ft (aircraft too large for standard spots)

/**
 * Egress option representing one possible exit path for a spot
 * Spots can have multiple egress lanes - aircraft chooses best available at departure
 */
export type EgressOption = {
  /** Egress lane identifier */
  laneId: string;
  /** Display name (e.g., "Lane 2") */
  laneName: string;
  /** Spots blocked if this lane is used */
  blocksSpotIds: string[];
};

// Orientation: discrete parking style with angle baked into id
export type Orientation = {
  id: string;
  label?: string;
  metadata?: Record<string, any>;
};

// Spot: single-aircraft location with time occupancy bitmap
export type Spot = {
  id: string;
  zoneId: string;
  orientations: Orientation[];
  allowedSizes: SizeCategory[]; // Aircraft sizes allowed in this spot (array-based policy)
  // Examples:
  // - ["large"] = large aircraft only (reserve big spots)
  // - ["small", "midsize"] = common jets only
  // - ["very-small", "small", "midsize", "super-midsize", "large"] = universal spot
  occupancy: BigUint64Array; // WORD_COUNT x 64-bit words == TOTAL_SLOTS bits

  // Multi-lane egress support
  egressOptions?: EgressOption[]; // Per-lane blocking relationships - aircraft chooses best at departure

  egressMasks?: {
    // Precomputed time windows when this spot is blocked/blocks others (Step 2 optimization)
    // Per-lane masks for multi-lane egress support
    perLane: Array<{
      laneId: string; // Lane identifier
      blocksSpotIds: string[]; // Spots blocked by this lane (cached from egressOptions)
      blocks: BigUint64Array; // Time windows when THIS lane blocks others
      blockedBy: BigUint64Array; // Time windows when THIS lane is blocked
    }>;
  };

  // Gap spot support (Phase 2a-2d)
  isGapSpot?: boolean; // Flag indicating this is a gap spot (becomes available when surrounding larger spots are occupied)
  requiresOccupiedSpots?: string[]; // Spot IDs that must be occupied for this gap spot to be available (Phase 2c/2d)

  // Duration limit for overflow/emergency spots
  max_dwell_minutes?: number; // Maximum parking duration in minutes (undefined = no limit)

  // Duration minimum for remote/long-term spots
  min_dwell_minutes?: number; // Minimum parking duration in minutes (undefined = no minimum)
  // WHY: Reserve remote/overflow spots for long-stay aircraft
  // LOGIC: Aircraft with dwell >= min_dwell_minutes get bonus (negative penalty)
  //        Aircraft with dwell < min_dwell_minutes get penalty (positive penalty)
  // EXAMPLE: Set 480 (8 hours) on remote spots to sequester long-stay aircraft

  // Disabled flag for maintenance/construction
  disabled?: boolean; // When true, spot is unavailable to parking algorithm (default: false)

  /**
   * Shape containing geometry and visual data
   * REQUIRED - every Spot is built from a Shape (eliminates shapes.find() lookups)
   */
  shape: Shape;
};

// Zone: operator-defined area grouping
export type Zone = {
  id: string;
  name: string;
  spotIds: string[];
  priority?: number; // Parking priority for this zone (0-10, from shape.zone_priority)
  shapeId?: string; // Reference to ramp.shapes[].id for visual representation
  disabled?: boolean; // When true, all spots in zone unavailable to parking algorithm (default: false)
};

/**
 * Temporal Lock - Hard constraint assigning aircraft to spot during time range
 *
 * Use cases:
 * - Maintenance windows (spot near maintenance facility)
 * - Fueling operations (spot near fuel truck)
 * - Boarding/deplaning (gate-adjacent spots)
 * - VIP service (premium spot reservation)
 */
export type TemporalLock = {
  /** Spot ID where aircraft must be parked during lock period */
  spotId: string;

  /** Lock start time (bucket index, inclusive) */
  startBucket: Bucket;

  /** Lock end time (bucket index, exclusive) */
  endBucket: Bucket;

  /** Human-readable reason for lock (for UI/logging) */
  reason?: string; // "maintenance", "fueling", "boarding", "vip"
};

// ParkingRequest: single aircraft with arrival/departure times
export type ParkingRequest = {
  id: string; // unique id of any kind (i.e. can be the tenant_id)
  sizeCategory: SizeCategory; // Aircraft size based on wingspan (determines spot compatibility)
  aircraft?: Aircraft; // full aircraft object for dimensions/geometry
  /**
   Time buckets are the temporal algorithm's way of discretizing time:
   - Each bucket represents a 15-minute time slot (4 buckets per hour)
   - Bucket 0 = planning window start (e.g., 00:00)
   - Bucket 32 = 8 hours later (32 / 4 = 8:00)
   - Bucket 96 = 24 hours later (full day)

   Example:
   - Aircraft arrives at 08:00 → arrivalBucket = 32 (8 hours × 4 buckets/hour)
   - Aircraft departs at 18:00 → departureBucket = 72 (18 hours × 4 buckets/hour)
   - Dwell time = buckets 32-72 (10 hours)
   */
  arrivalBucket: Bucket;
  departureBucket: Bucket;
  tail_number: string; // used for logging/explainability only

  /**
   * Optional time-bounded spot locks
   *
   * Locks are hard constraints that force the aircraft to specific spots
   * during specific time ranges. The algorithm will:
   * - Place aircraft in locked spot during lock period (or fail)
   * - Optimize placement before/after lock periods
   * - Validate locks (availability, size compatibility)
   *
   * Multiple locks are supported for complex schedules:
   * - Locks must not overlap each other
   * - Locks can span partial or full dwell
   * - Non-locked time uses normal optimization
   *
   * Example:
   * locks: [
   *   { spotId: "M1", startBucket: 40, endBucket: 56, reason: "maintenance" },
   *   { spotId: "F3", startBucket: 60, endBucket: 64, reason: "fueling" }
   * ]
   */
  locks?: TemporalLock[];

  /**
   * Optional trip ID for database persistence
   * Used by application layer to map placements back to trips
   * The parking algorithm ignores this field
   */
  trip_id?: string;

  /**
   * Optional priority for placement (1-10, default 5)
   * Higher priority = harder to move in last-minute insertion scenarios
   */
  priority?: number;

  /**
   * Optional metadata for tracking move history and other attributes
   * Used by last-minute insertion for fairness scoring
   */
  metadata?: {
    moveHistory?: Array<{ timestamp: number }>;
    [key: string]: any;
  };

  /**
   * Optional locked until bucket for temporary immobility
   * Aircraft locked until this bucket cannot be moved
   * Used by last-minute insertion moveability scoring
   */
  lockedUntilBucket?: number;

  /**
   * Optional previous spot ID for stickiness scoring
   * When set, algorithm penalizes placing aircraft in different spot
   * Used by last-minute insertion to preserve existing schedule
   */
  prevSpotId?: string;

  /**
   * Optional staging segment flag for staging area workflow
   *
   * When true, this request segment MUST be placed in a staging zone
   * (spot.shape.metadata.is_staging === true).
   *
   * This flag is set automatically by the staging area feature when it
   * generates multi-segment trips (arrival → staging → permanent → staging → departure).
   *
   * Staging segments receive special treatment:
   * - Filtered to staging zones only (hard constraint)
   * - Exempt from waste penalty (small aircraft can use large staging spots)
   * - Moves between staging and permanent don't count toward plannedMoveCap
   *
   * Example workflow for 10-hour stay with staging enabled:
   * - Segment 1: arrivalBucket=0, departureBucket=3, isStagingSegment=true (45 min)
   * - Segment 2: arrivalBucket=3, departureBucket=37 (9h 10min permanent)
   * - Segment 3: arrivalBucket=37, departureBucket=40, isStagingSegment=true (45 min)
   *
   * Pairs with: spot.shape.metadata.is_staging (zone property)
   */
  isStagingSegment?: boolean;

  /**
   * Placement source (manual/auto/import) for visual indicators
   * Shows how the placement was created (manual drag, AutoPark, or external import)
   */
  placement_source?: 'manual' | 'auto' | 'import';

  /**
   * Whether placement is locked from AutoPark modifications
   * Locked placements are shown as fixed constraints in visualizations
   */
  is_locked?: boolean;
};

// Placement segment: one time range at one spot
export type PlacementSegment = {
  spotId: string;
  orientationId: string;
  startBucket: Bucket;
  endBucket: Bucket;
};

// Placement: output of placement algorithm
// Can be either:
// - Single segment (full dwell in one spot): { spotId, orientationId }
// - Multiple segments (planned moves): { segments: [...], moveCount: number }
export type Placement = Record<
  string,
  | {
      spotId: string;
      orientationId: string;
    }
  | {
      segments: PlacementSegment[];
      moveCount: number; // Actual number of spot changes (not segment count)
    }
>;

/**
 * Breakdown of how spots were filtered during placement attempt
 * Tracks cumulative elimination at each filter stage
 */
export type FilterBreakdown = {
  total: number; // Total spots considered
  disabled: number; // Spots/zones marked as disabled
  stagingIncompatible: number; // Staging segment can't use non-staging spot (or vice versa)
  sizeIncompatible: number; // Aircraft size doesn't fit spot's allowed sizes
  timeConflict: number; // Spot occupied during aircraft's dwell period
  durationExceeded: number; // Aircraft dwell exceeds spot's max_dwell_minutes
  geometryCollision: number; // Gap spot blocked by existing aircraft (Phase 3)
  available: number; // Final count of available spots
};

/**
 * Size analysis breakdown for spots vs aircraft requirements
 */
export type SizeAnalysis = {
  aircraftSize: SizeCategory;
  aircraftWingspan?: number; // feet
  aircraftLength?: number; // feet
  spotsBySize: Record<SizeCategory, number>; // Count of spots per size category
  minimumNeeded: number; // Minimum spots of aircraft's size needed
  currentAvailable: number; // Current spots of aircraft's size
};

/**
 * Time congestion analysis for scheduling conflicts
 */
export type TimeAnalysis = {
  aircraftArrivalBucket: Bucket;
  aircraftDepartureBucket: Bucket;
  dwellDurationBuckets: number; // Total dwell in buckets (15min each)
  dwellDurationMinutes: number; // Total dwell in minutes
  peakCongestionStart?: Bucket; // When congestion peaks
  peakCongestionEnd?: Bucket; // When congestion ends
  peakOccupiedSpots?: number; // Max spots occupied during peak
  conflictWindowBuckets?: number; // Duration of conflict in buckets
};

/**
 * Duration limit analysis for overflow spot restrictions
 */
export type DurationAnalysis = {
  requiredDwellMinutes: number; // Aircraft's actual dwell requirement
  spotsWithLimits: number; // Count of spots with max_dwell_minutes
  mostRestrictiveLimit?: number; // Smallest max_dwell_minutes among spots
  excessDuration?: number; // How much aircraft exceeds limit (minutes)
};

/**
 * Actionable suggestion for resolving placement failure
 * Based on best practices from Rust compiler, Kubernetes, PostgreSQL EXPLAIN
 */
export type ActionableSuggestion = {
  priority: number; // 1 = highest priority
  category: "size" | "time" | "duration" | "disabled" | "geometry"; // Which filter this addresses
  title: string; // Short action title
  description: string; // Detailed explanation
  impact?: string; // Expected improvement (e.g., "+15 spots available")
  specificAction?: string; // Exact action to take
};

/**
 * Near-miss spot - A spot that almost worked but failed one specific filter
 * Inspired by Gurobi's Irreducible Inconsistent Subsystem (IIS) concept
 */
export type NearMissSpot = {
  spotId: string;
  failedFilter: "disabled" | "size" | "time" | "duration" | "geometry";
  passedFilters: string[]; // List of filters this spot passed
  specificIssue: string; // Exact reason (e.g., "Needs 180 min, limit: 60 min")
  easyFix?: string; // Quick fix suggestion (e.g., "Enable spot")
};

/**
 * Enhanced suggestion details with analysis and actionable recommendations
 * Inspired by industry best practices:
 * - Rust compiler: Educational, visual, specific
 * - Kubernetes: Numeric specificity, resource comparisons
 * - PostgreSQL: Cost metrics, performance projections
 * - Gurobi: Minimal conflict sets, relaxation guidance
 */
export type SuggestionDetails = {
  primaryBlocker: "disabled" | "size" | "time" | "duration" | "geometry" | "none";
  sizeAnalysis?: SizeAnalysis;
  timeAnalysis?: TimeAnalysis;
  durationAnalysis?: DurationAnalysis;
  nearMisses?: NearMissSpot[]; // Spots that almost worked (Phase 4)
  suggestions: ActionableSuggestion[]; // Ordered by priority
};

// Decision log entry for explainability
export type LogEntry = {
  aircraftId: string;
  spotId?: string;
  type: "eval" | "placed" | "rejected" | "segmented" | "moved" | "section-header";
  timeAvailable?: boolean;
  trappedCount?: number;
  reason?: string;

  // Move tracking (for last-minute insertion disruption)
  fromSpot?: string; // Original spot (for moves)
  toSpot?: string; // New spot (for moves)
  moveReason?: string; // Why this aircraft was moved

  // Filter breakdown for rejected placements
  filterBreakdown?: FilterBreakdown;

  // Enhanced suggestion details (Phase 1: Numeric Specificity)
  suggestionDetails?: SuggestionDetails;

  // Multi-lane egress tracking
  chosenEgressOption?: {
    laneId: string;
    laneName: string;
    blocksCount: number; // How many spots this lane blocks
  };
  availableEgressOptions?: number; // Total lanes available

  blockedSpots?: string[]; // spot IDs that would be blocked
  blockedAircraft?: string[]; // aircraft IDs that would be trapped
  blockingDetails?: Array<{
    spotId: string; // Blocked spot ID
    occupiedBy?: string; // Aircraft tail number at this spot (if occupied)
    isTrapped: boolean; // Does this placement trap this aircraft?
    reason: string; // Explanation (e.g., "No trap (departs 02:15 < 04:30)")
  }>;
  egressScore?: number; // weighted score (trappedCount × egressWeight)
  egressWeight?: number; // weight multiplier applied
  sizeMismatch?: boolean; // true if placement rejected due to size incompatibility
  spotAllowedSizes?: SizeCategory[]; // sizes allowed by the spot
  aircraftSize?: SizeCategory; // size of the aircraft being placed

  // Structural complexity penalty tracking
  structuralComplexity?: number; // 0-N count of spots blocked by best egress lane
  structuralWeight?: number; // weight multiplier (only logged if non-default)
  structuralPenalty?: number; // structuralComplexity × structuralWeight

  // Waste penalty tracking
  wasteScore?: number; // 0-4 raw waste value (spot max size - aircraft size)
  wasteWeight?: number; // weight multiplier (only logged if non-default)
  wastePenalty?: number; // wasteScore × wasteWeight

  // Zone priority tracking
  zonePriority?: number; // Priority of zone containing this spot (0-10, default 5)
  zonePriorityWeight?: number; // Weight multiplier (only logged if non-default ≠ 1.0)
  zonePenalty?: number; // (10 - zonePriority) × zonePriorityWeight

  // Stickiness tracking (last-minute insertion)
  prevSpotId?: string; // Previous spot ID (if aircraft was already placed)
  stickyPenalty?: number; // Penalty for moving from prevSpot (0 if staying put)
  stickinessWeight?: number; // Weight multiplier (only logged if non-default ≠ 0)

  // Duration limit tracking
  duration_exceeded?: boolean; // true if spot rejected due to duration limit
  max_dwell_minutes?: number; // Spot's max dwell limit (minutes)
  actual_dwell_minutes?: number; // Aircraft's actual dwell duration (minutes)

  // Min dwell time tracking
  min_dwell_minutes?: number; // Spot's min dwell requirement (minutes)
  min_dwell_penalty?: number; // Penalty/bonus for min dwell match
  min_dwell_weight?: number; // Weight multiplier (always logged)

  // Disabled status tracking
  spot_disabled?: boolean; // true if spot is directly disabled
  zone_disabled?: boolean; // true if spot's zone is disabled

  // Gap spot tracking
  isGapSpot?: boolean; // true if this spot is a gap spot
  gapRequiredSpots?: string[]; // spot IDs that must be occupied for gap spot to be available
  gapDependenciesMet?: number; // how many required spots are occupied
  gapDependenciesTotal?: number; // total number of required spots
  gapCollision?: boolean; // true if geometry collision detected (Phase 3)

  // Mask optimization debug info (Phase 9)
  maskDebug?: {
    usedMasks: boolean; // true = used mask optimization, false = used loop fallback
    maskResult?: number; // Trap count from mask-based calculation
    loopResult?: number; // Trap count from loop-based calculation (if validation enabled)
    matched?: boolean; // Did mask and loop results match? (if validation enabled)
  };

  /** Lock constraint information (for locked segments) */
  lockConstraint?: {
    spotId: string; // Spot required by lock
    reason?: string; // Lock reason ("maintenance", etc.)
    isLocked: boolean; // true for locked segment
    lockValidated: boolean; // Was lock constraint satisfied?
  };
};

// Planning stats
export type PlanningStats = {
  totalAircraft: number;
  placed: number;
  unplaced: number;
  totalMovements: number; // unused in v1
  plannedMoves: number; // count of segmented placements (aircraft with >1 segment)

  // Egress blocking statistics
  trappingCount?: number; // How many aircraft trap others
  // WHY: Tells operator "3 problematic placements"
  // EXAMPLE: 3 aircraft block egress for others

  totalTrappedAircraft?: number; // Sum of all trapped aircraft
  // WHY: Tells operator total impact
  // EXAMPLE: Those 3 aircraft block 8 others total

  // LNS statistics (if LNS was run)
  lnsIterations?: number;
  lnsTimeMs?: number;
  lnsImproved?: boolean;
};

// Planning result
export type PlanningResult = {
  placement: Placement;
  stats: PlanningStats;
  unplacedAircraftIds: string[];
  log: LogEntry[];

  // Disruption stats (for last-minute insertion)
  disruptionStats?: {
    movedAircraft: string[]; // IDs of aircraft that were moved
    totalMoves: number; // Total number of moves performed
    cascadeDepth: number; // Max cascade depth encountered
  };
};

// Planning result with additional data (used in UI)
export type PlanningResultWithData = PlanningResult & {
  aircraft: ParkingRequest[];
  spots: Spot[];
  zones: Zone[];
  horizonHours: number;
  startTime: string;
};
